import { reactive } from 'vue'
import { Game } from './index'
import './handlers'
import { defaultGameData } from '../data'
import type { itemId } from '../data/types'
import { seededRandom } from '../lib/algo'

export const game = new Game(defaultGameData, reactive({
    tick: 0,
    items: new Map(),
    tiles: new Map(),
    size: { x: 15, y: 10 },
    dragging: null,
    hoveredTile: null,
    highlight: null,
    scheduledEvents: [],
    visibilityThreshold: 1,  // starts at orb-1 power
    gameOver: 'playing' as const
}))

Object.assign(globalThis, { game })

game.initializeGrid(25, 13)

// Initialize shade on edges and place emitters
function initializeShadeMap() {
    const { x: width, y: height } = game.state.size
    const centerX = Math.floor(width / 2)
    const centerY = Math.floor(height / 2)

    // Add shade based on distance from center (high values for viscous flow)
    for (const tile of game.state.tiles.values()) {
        const dx = Math.abs(tile.position.x - centerX)
        const dy = Math.abs(tile.position.y - centerY)
        const distFromCenter = Math.max(dx, dy)

        // Shade increases toward edges (values >= 100 can flow)
        if (distFromCenter >= 4) {
            tile.shade = 100 + (distFromCenter - 4) * 50
        } else if (distFromCenter >= 3) {
            tile.shade = 50  // Low, won't flow into center
        }
    }

    // Place emitters in corners (covered by shade)
    const emitterPositions = [
        { x: 1, y: 1 },
        { x: width - 2, y: 1 },
        { x: 1, y: height - 2 },
        { x: width - 2, y: height - 2 },
    ]

    emitterPositions.forEach((pos, i) => {
        const emitterId = i < 2 ? 'emitter-1' : 'emitter-2'
        game.createItem(emitterId as itemId, { position: pos })
    })

    // Place chests at edges
    const lootPositions = [
        { x: 0, y: centerY, item: 'chest-1' },
        { x: width - 1, y: centerY, item: 'chest-1' },
    ]

    lootPositions.forEach(({ x, y, item }) => {
        const tile = game.getTileAt({ x, y })
        if (tile && !tile.item) {
            game.createItem(item as itemId, { position: { x, y } })
        }
    })

    // Scatter flower-1 items in shaded areas using seeded random
    const rng = seededRandom(12345)  // Use consistent seed for reproducibility
    const shadedTiles = Array.from(game.state.tiles.values()).filter(tile =>
        tile.shade > 0 && !tile.item
    )

    // Place flowers in ~50% of shaded tiles
    const flowerCount = Math.floor(shadedTiles.length * 0.5)
    const shuffled = shadedTiles.sort(() => rng() - 0.5)

    for (let i = 0; i < Math.min(flowerCount, shuffled.length); i++) {
        const tile = shuffled[i]
        if (tile && !tile.item) {
            game.createItem('flower-1', { position: tile.position })
        }
    }
}

// Initialize player starting area (center, no shade)
function initializePlayerArea() {
    const { x: width, y: height } = game.state.size
    const centerX = Math.floor(width / 2)
    const centerY = Math.floor(height / 2)

    // Starting items for the player
    const startingItems: { dx: number, dy: number, item: itemId }[] = [
        { dx: -1, dy: 0, item: 'flower-1' as itemId },
        { dx: 1, dy: 0, item: 'flower-1' as itemId },
        { dx: 0, dy: -1, item: 'orb-1' as itemId },
        { dx: 0, dy: 1, item: 'orb-1' as itemId },
        { dx: 0, dy: 0, item: 'orb-1' as itemId },
    ]

    startingItems.forEach(({ dx, dy, item }) => {
        const pos = { x: centerX + dx, y: centerY + dy }
        const tile = game.getTileAt(pos)
        if (tile && !tile.item) {
            game.createItem(item, { position: pos })
        }
    })
}

// Run initialization
initializeShadeMap()
initializePlayerArea()

// Debug: also keep the row 0 spawning for testing (can be removed later)
const DEBUG_MODE = false

if (DEBUG_MODE) {
    const spawnableItems = Object.values(game.data.items)
        .filter(item => item.display)
        .map(item => item.id)

    function spawnRow0() {
        spawnableItems.forEach((id, x) => {
            const tile = game.getTileAt({ x, y: 0 })
            if (tile && !tile.item) {
                game.createItem(id, { position: { x, y: 0 } })
            }
        })
    }

    spawnRow0()

    game.on('manual:drop', ({ from }) => {
        if (from && from.position.y === 0) {
            const tile = game.getTileAt({ x: from.position.x, y: 0 })
            if (tile && !tile.item) {
                const id = spawnableItems[from.position.x]
                if (id) game.createItem(id, { position: { x: from.position.x, y: 0 } })
            }
        }
    })
}
